// Typed API client for the fabric status vocabulary.
import api from "./api";
import { IFabricStatus, StatusVisibility } from "@/models/fabric_status";

export default class FabricStatusService {
  /**
   * In-memory cache. The vocabulary is a handful of docs that changes a few times
   * a year, but three separate views need it (editor, add form, list filters).
   * Without this, navigating between them would refetch it every time.
   *
   * Shared at class level, and invalidated on every write below, so a status
   * created in the editor is immediately visible to the list's filter chips.
   */
  private static cache: IFabricStatus[] | null = null;
  private static inflight: Promise<IFabricStatus[]> | null = null;

  public static invalidate(): void {
    FabricStatusService.cache = null;
    FabricStatusService.inflight = null;
  }

  // GET /fabric-statuses
  public async list(force = false): Promise<IFabricStatus[]> {
    if (!force && FabricStatusService.cache) return FabricStatusService.cache;
    // De-duplicate concurrent callers — the editor mounts two components that
    // both want the vocabulary, and we don't need two requests for one answer.
    if (!force && FabricStatusService.inflight) return FabricStatusService.inflight;

    const req = api.get("/fabric-statuses").then((res) => {
      const data = (res.data?.data ?? []) as IFabricStatus[];
      FabricStatusService.cache = data;
      FabricStatusService.inflight = null;
      return data;
    });
    FabricStatusService.inflight = req;
    return req;
  }

  // POST /fabric-statuses — `handle` is derived server-side from the label.
  public async create(payload: {
    label: string;
    note?: string;
    visibility?: StatusVisibility;
  }): Promise<IFabricStatus> {
    const res = await api.post("/fabric-statuses", payload);
    FabricStatusService.invalidate();
    return res.data.data as IFabricStatus;
  }

  // PUT /fabric-statuses/:id — label/note/visibility. The handle never moves.
  public async update(id: string, payload: Partial<IFabricStatus>): Promise<IFabricStatus> {
    const res = await api.put(`/fabric-statuses/${id}`, payload);
    FabricStatusService.invalidate();
    return res.data.data as IFabricStatus;
  }

  // DELETE /fabric-statuses/:id — refused by the API if any fabric still uses it.
  public async remove(id: string): Promise<void> {
    await api.delete(`/fabric-statuses/${id}`);
    FabricStatusService.invalidate();
  }

  // POST /fabric-statuses/seed — idempotently create the canonical statuses.
  public async seed(): Promise<{ created: string[]; existing: string[] }> {
    const res = await api.post("/fabric-statuses/seed");
    FabricStatusService.invalidate();
    return res.data.data;
  }
}
